export const CaseQuiz = {
  namespaced: true,

  state: {
    loading: false,
    currentQuiz: null,
    selectedCase: 1,
    quizCases: [
      {
        title: '',
        caseId: null,
        position: 1,
        forms: [],
      },
    ],
    quizStarted: {
      id: null,
      answeredQuizCaseIndex: 0,
      cases: [],
      caseAnswers: {},
    },
    quizGroupId: null,
    isQuizAuthor: false,
    examData: {
      passGrade: 0,
    },
    examCaseAnswers: {},
    examResults: {},
    allExamResults: [],
    lastAnsweredCaseId: null,
  },

  getters: {
    getAllExamRes(state) {
      return state.allExamResults;
    },
    getExamRes(state) {
      return state.examResults;
    },
    getExamCaseAnswers(state) {
      return state.examCaseAnswers;
    },
    getExamData(state) {
      return state.examData;
    },
    getQuizCases(state) {
      return state.quizCases;
    },
    getStatus(state) {
      return state.loading;
    },
    getQuizList: (state) => {
      return state.quizList;
    },
    getCurrentQuiz: (state) => {
      return state.currentQuiz;
    },
    getQuizStartedId: (state) => () => {
      return state.quizStarted.id;
    },
    getAnsweredQuizCaseIndex: (state) => () => {
      return state.quizStarted.answeredQuizCaseIndex;
    },
    getQuizCaseAnswers: (state) => () => {
      return state.quizStarted.caseAnswers;
    },
  },

  mutations: {
    setAllExamResults(state, allExamResults) {
      state.allExamResults = allExamResults;
    },
    setExamResults(state, examResults) {
      state.examResults = examResults;
    },
    setExamData(state, exam) {
      state.examData = exam;
    },

    removeQuizCase(state, quizCaseId) {
      if (!quizCaseId) {
        state.quizCases.pop();
      }
      state.quizCases = state.quizCases.filter((quizCase) => quizCase.id !== quizCaseId);
    },
    setNewQuizCase(state) {
      const newCasePosition = state.quizCases[state.quizCases.length - 1].position + 1;
      state.quizCases.push({
        title: '',
        caseId: null,
        position: newCasePosition > 0 ? newCasePosition : 1,
        forms: [],
      });
    },
    setBusy(state, loading) {
      state.loading = loading;
    },
    setQuiz(state, quiz) {
      state.currentQuiz = quiz;
    },
    setQuizCases(state, cases) {
      state.quizCases = [...cases];
    },
    setQuizStarted(state, quizData) {
      state.quizStarted = { ...state.quizStarted, ...quizData };
    },
    setStartedQuizCases(state, cases) {
      state.quizStarted.cases = cases;
    },
    setQuizList(state, quizData) {
      state.quizList = [...state.quizList, ...quizData];
    },
    setAnsweredQuizCaseIndex(state, index) {
      state.quizStarted.answeredQuizCaseIndex = index;
    },
    setQuizCaseAnswers(state, { caseId, answers }) {
      state.quizStarted.caseAnswers[caseId] = answers;
    },
    setExamCaseAnswers(state, { caseId, answers }) {
      state.examCaseAnswers[caseId] = answers;
    },
    setExamCaseAnswersFields(state, { caseId, answers: { formId, fields } }) {
      const examCase = state.examCaseAnswers[caseId];

      for (const form of examCase) {
        if (form.formId === formId) {
          form.fields = fields;
          break;
        }
      }
    },
    setQuizGroupId(state, id) {
      state.quizGroupId = id;
    },
    setIsQuizAuthor(state, id) {
      state.isQuizAuthor = id;
    },
    setLastAnsweredCaseId(state, id) {
      state.lastAnsweredCaseId = id;
    },
  },

  actions: {
    //EXAM ACTIONS

    async deleteExamCaseAnswers(_, { groupId, quizId, quizCaseId }) {
      try {
        await this._vm.$api('api.platform.groups.quizzes.exam.remove.case.answer', {
          caseGroup: groupId,
          quiz: quizId,
          quizCase: quizCaseId,
          version: '1.0',
        });
      } catch (err) {
        console.log(err);
      }
    },

    async saveExamAnswers({ commit }, { groupId, quizId, examId, data }) {
      try {
        const res = await this._vm.$api(
          'api.platform.groups.quizzes.exam.put.all.answer',
          {
            caseGroup: groupId,
            quiz: quizId,
            exam: examId,
            version: '1.0',
          },
          data
        );
        commit('setExamData', res.data.data);

        return res;
      } catch (err) {
        console.log(err);
      }
    },

    async fetchExam({ commit }, { groupId, quizId }) {
      try {
        const res = await this._vm.$api('api.platform.groups.quizzes.exam.get', {
          version: '1.0',
          caseGroup: groupId,
          quiz: quizId,
        });
        commit('setExamData', res.data.data);
      } catch (err) {
        console.log(err);
        return err;
      }
    },

    async fetchExamResult({ commit }, { groupId, quizId, userId }) {
      commit('setBusy', true);
      try {
        const res = await this._vm.$api('api.platform.groups.quizzes.exam.result.get', {
          caseGroup: groupId,
          quiz: quizId,
          student: userId,
        });

        commit('setExamResults', res.data.data);
        commit('setBusy', false);
      } catch (err) {
        console.log(err);
      }
    },

    async fetchAllExamResults({ commit }, { groupId, quizId }) {
      commit('setBusy', true);
      try {
        const res = await this._vm.$api('api.platform.groups.quizzes.exam.result.get.all', {
          caseGroup: groupId,
          quiz: quizId,
        });

        commit('setAllExamResults', res.data.data);
        commit('setBusy', false);
      } catch (err) {
        console.log(err);
      }
    },

    //QUIZ ACTIONS
    async downloadQuizResults(_, { groupId, quizId }) {
      try {
        await this._vm.$api('groups.quizzes.results', { caseGroup: groupId, id: quizId });
      } catch (err) {
        console.log(err);
      }
    },

    async fetchQuizCases({ commit }, { groupId, quizId }) {
      commit('setBusy', true);
      try {
        const res = await this._vm.$api('groups.quizzes.cases.index', { caseGroup: groupId, quiz: quizId });
        if (res.data.data.length) {
          commit('setQuizCases', res.data.data);
        }
        commit('setBusy', false);
      } catch (err) {
        console.log(err);
      }
    },

    async getQuiz({ commit }, { groupId, quizId, author }) {
      commit('setBusy', true);
      try {
        const res = await this._vm.$api('groups.quizzes.get', { caseGroup: groupId, id: quizId, author: author });
        if (res) {
          commit('setQuiz', res.data.data);
          commit('setBusy', false);
          commit('setQuizGroupId', groupId);
        }
      } catch (err) {
        console.log(err);
      }
    },

    async saveQuizCases(_, { groupId, quizId, data }) {
      let casePromises = [];
      let store = this;
      data.forEach(function (quizCase) {
        casePromises.push(
          new Promise((resolve) => {
            let { id } = quizCase;
            const res = store._vm.$api('groups.quizzes.cases.edit', { caseGroup: groupId, quiz: quizId, id }, quizCase);
            resolve(`Completed in ${quizCase.title}`);
          })
        );
      });
      Promise.all(casePromises);
    },

    async deleteQuizCase({ commit }, { groupId, quizId, quizCaseId }) {
      if (quizCaseId) {
        try {
          await this._vm.$api('groups.quizzes.cases.delete', {
            caseGroup: groupId,
            quiz: quizId,
            id: quizCaseId,
          });
        } catch (err) {
          console.log(err);
        }
      }
      commit('removeQuizCase', quizCaseId);
    },

    async createQuizCase({ commit }, { groupId, quizId, data }) {
      if (!data.length) return;
      try {
        const filteredData = data?.filter((quizCase) => !quizCase.id)[0];
        if (filteredData) {
          commit('setBusy', true);
          const res = await this._vm.$api(
            'groups.quizzes.case.create',
            { caseGroup: groupId, quiz: quizId },
            filteredData
          );

          commit('setBusy', false);
          return res.status;
        }
        return 201;
      } catch (err) {
        console.log(err);
      }
    },

    async removeQuiz({ dispatch }, data) {
      try {
        const res = await this._vm.$api('groups.quizzes.delete', {
          caseGroup: data.caseGroup,
          id: data.id,
        });
        if (res) {
          dispatch('caseQuizList/removeQuizFromList', data.id, { root: true });
        }
        return res;
      } catch (err) {
        console.log(err);
      }
    },

    async createNewQuiz(_, { groupId, obj }) {
      try {
        const res = await this._vm.$api('groups.quizzes.create', { caseGroup: groupId }, obj);
        if (res && res.data) return res.data;
      } catch (err) {
        console.log(err);
      }
    },

    async editQuiz(_, { groupId, quizId, obj }) {
      try {
        const res = await this._vm.$api('groups.quizzes.edit', { caseGroup: groupId, id: quizId }, obj);
        return res;
      } catch (err) {
        console.log(err);
      }
    },

    addNewQuizCase({ commit }) {
      commit('setNewQuizCase');
    },

    updateQuizCases({ commit }, cases) {
      commit('setQuizCases', cases);
    },

    updateQuizStarted({ commit }, quizStarted) {
      commit('setQuizStarted', quizStarted);
    },

    updateStartedQuizCases({ commit }, cases) {
      commit('setStartedQuizCases', cases);
    },

    updateAnsweredQuizCaseIndex({ commit }, index) {
      commit('setAnsweredQuizCaseIndex', index);
    },

    updateQuizCaseAnswers({ commit }, { caseId, answers }) {
      commit('setQuizCaseAnswers', { caseId, answers });
    },

    updateExamCaseAnswers({ commit }, { caseId, answers }) {
      commit('setExamCaseAnswers', { caseId, answers });
    },

    updateExamCaseAnswersFields({ commit }, { caseId, answers }) {
      commit('setExamCaseAnswersFields', { caseId, answers });
    },

    updateIsQuizAuthor({ commit }, boolean) {
      commit('setIsQuizAuthor', boolean);
    },

    updateLastAnsweredCaseId({ commit }, id) {
      commit('setLastAnsweredCaseId', id);
    },
  },
};
